import { Component, ElementRef, HostListener, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, computed, signal, input, output } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { Router } from '@angular/router'
import { Projekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { LaskunListaustietorivi, LaskunListaustyyppi, LaskunTila, LaskutHakuPyynto, TypeaheadAsiakas, TypeaheadAsiakasDokumentti } from '@lemon/core-firebase/model/lasku'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskutusHakuStateService } from 'app/_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { ProjektitService } from 'app/_jaettu-angular/projektit.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { Subject, firstValueFrom, of } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'
import { LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData } from '../../_angular/_resolvers/lasku.resolve'
import { LaskuHakuDataSourceLemonator } from './lasku-haku-lemonator.datasource'

interface TilaChip {
  tila: LaskunTila
  nimi: string
}

interface ValittuRajaus {
  avain: string
  nimi: string
}

type LaskuHakuPaneeli = 'aikavali' | 'tila' | 'asiakas' | 'projekti'

const HAETTAVAT_TILAT: LaskunTila[] = [
  LaskunTila.avoin,
  LaskunTila.eraantynyt,
  LaskunTila.luonnos,
  LaskunTila.maksettu,
  LaskunTila.hyvitetty,
  LaskunTila.maksettuLiikaa,
  LaskunTila.luottotappio,
  LaskunTila.mitatoity,
  LaskunTila.poistettu,
]

@Component({
  selector: 'app-lasku-haku-lemonator',
  templateUrl: './lasku-haku-lemonator.component.html',
  styleUrls: ['./lasku-haku-lemonator.component.css'],
  standalone: false,
})
export class LaskuHakuLemonatorComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  // Kenttä on @if-lohkon sisällä, joten setter laukeaa vasta kun dialogi avataan ja kenttä ilmestyy DOMiin
  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      setTimeout(() => kentta.nativeElement.focus())
    }
  }

  hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  viimeiset12kkAktiivinen = true
  alkuPvm = form(signal<Date | null>(null))
  loppuPvm = form(signal<Date | null>(null))
  readonly valittuAlkuPvm = computed(() => this.alkuPvm().value())
  readonly valittuLoppuPvm = computed(() => this.loppuPvm().value())
  readonly aikavalinTeksti = signal('')
  avoinPaneeli: LaskuHakuPaneeli | null = null

  readonly kaikkiTilat = signal<TilaChip[]>([])
  valitutTilat = new Set<LaskunTila>()

  readonly kaikkiAsiakkaat = signal<TypeaheadAsiakas[]>([])
  valitutAsiakkaat = new Set<string>()
  asiakasRajaus = form(signal(''))

  readonly kaikkiProjektit = signal<Projekti[]>([])
  valitutProjektit = new Set<string>()
  naytaSummatVerottomina = false
  readonly naytaProjektitSuodatin = signal(false)

  readonly tulokset = signal<LaskunListaustietorivi[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  readonly yhteissummaSisAlv = signal<number | null>(null)
  readonly yhteissummaAlv0 = signal<number | null>(null)
  sivu = 0
  sivunKoko = 50
  readonly sivukoot = [50, 100, 200]

  private _tilaNimet = new Map<string, string>()
  private _asiakasNimet = new Map<string, string>()
  private _projektiNimet = new Map<string, string>()
  private _ngUnsubscribe = new Subject<void>()
  private _nykyinenKieli: TuettuKieli | null = null

  constructor(
    private _dataSource: LaskuHakuDataSourceLemonator,
    private _dateService: DateService,
    private _asiakasService: AsiakasService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _laskuUriService: LaskuUriService,
    private _projektitService: ProjektitService,
    private _laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private _hakuStateService: LaskutusHakuStateService,
    private _router: Router,
    private _translationService: LemonTranslationService,
    private _currencyService: CurrencyService,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      this._nykyinenKieli = kieli
      this.kaikkiTilat.set(HAETTAVAT_TILAT.map(tila => ({ tila: tila, nimi: this._translationService.lokalisoiKielella(`lasku.listaus.tila.${tila}`, kieli) })))
      this._tilaNimet = new Map(this.kaikkiTilat().map(tila => [tila.tila, tila.nimi]))
      this._paivitaAikavalinTeksti()
    })

    this._dataSource.dataSourceTransformed
      .connect(null)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(rivit => {
        this.tulokset.set(rivit)
        this._paivitaYhteissummat()
      })
    this._dataSource.lataaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(lataa => this.haetaan.set(lataa))
    this._dataSource.kokonaismaaraObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kokonaismaara => {
      this.kokonaismaara.set(kokonaismaara)
      this._paivitaYhteissummat()
    })

    const tallennettuHakutila = this._hakuStateService.lasku.onkoHakutilaTallennettu(this._router.url) ? this._hakuStateService.lasku.palautaHakutila() : null
    if (tallennettuHakutila) {
      asetaArvo(this.hakuteksti, tallennettuHakutila.hakuteksti, { ilmoita: false })
      this.viimeiset12kkAktiivinen = tallennettuHakutila.viimeiset12kkAktiivinen
      asetaArvo(this.alkuPvm, tallennettuHakutila.alkuPvm ? this._dateService.numberToDate(tallennettuHakutila.alkuPvm) : null)
      asetaArvo(this.loppuPvm, tallennettuHakutila.loppuPvm ? this._dateService.numberToDate(tallennettuHakutila.loppuPvm) : null)
      this.valitutTilat = new Set(tallennettuHakutila.valitutTilat)
      this.valitutAsiakkaat = new Set(tallennettuHakutila.valitutAsiakkaat)
      this.naytaSummatVerottomina = tallennettuHakutila.naytaSummatVerottomina
      this.valitutProjektit = new Set(tallennettuHakutila.valitutProjektit)
      this.sivu = tallennettuHakutila.sivu
      this.sivunKoko = tallennettuHakutila.sivunKoko
      this._paivitaAikavalinTeksti()
      this._hae()
    }

    this._asiakasService.nykyinenAsiakasAvainObservable
      .pipe(
        switchMap(avainTiedot => {
          if (!avainTiedot) {
            return of<TypeaheadAsiakasDokumentti>(null)
          }
          const uri = this._laskuUriService.getTypeaheadAsiakasDokumentinUriNew(avainTiedot.avain)
          return this._firebaseLemonaid.firestoreDoc<TypeaheadAsiakasDokumentti>(uri).listen()
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(dokumentti => {
        const asiakkaat = dokumentti ?? {}
        this.kaikkiAsiakkaat.set(
          Object.keys(asiakkaat)
            .map(avain => ({ avain: avain, nimi: asiakkaat[avain].nimi, ytunnus: asiakkaat[avain].ytunnus }))
            .sort((a, b) => (a.nimi ?? '').localeCompare(b.nimi ?? '', 'fi')),
        )
        this._asiakasNimet = new Map(this.kaikkiAsiakkaat().map(asiakas => [asiakas.avain, asiakas.nimi]))
      })

    this._projektitService.naytaProjektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(nayta => {
      this.naytaProjektitSuodatin.set(nayta)
      if (!nayta && this.valitutProjektit.size > 0) {
        this.valitutProjektit.clear()
        if (this.avoinPaneeli === 'projekti') {
          this.avoinPaneeli = null
        }
        this._haeAlusta()
      }
    })

    this._projektitService.naytettavatKirjanpitoprojektitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(projektit => {
      this.kaikkiProjektit.set(projektit ?? [])
      this._projektiNimet = new Map(this.kaikkiProjektit().map(projekti => [projekti.avain, projekti.nimi]))
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hae()
    } else {
      this._dataSource.hae(null)
      this._suljePaneelit()
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (!this.avoinna()) {
      return
    }
    if (this._onkoJokinPaneeliAuki()) {
      this._suljePaneelit()
    } else {
      this.sulje()
    }
  }

  @HostListener('document:click', ['$event'])
  dokumenttiaKlikattu(event: MouseEvent) {
    if (!this.avoinna() || !this._onkoJokinPaneeliAuki()) {
      return
    }
    const target = event.target as HTMLElement | null
    if (!target || target.closest('.dropdown-wrap') || target.closest('.cdk-overlay-container')) {
      return
    }
    this._suljePaneelit()
  }

  private _onkoJokinPaneeliAuki(): boolean {
    return this.avoinPaneeli !== null
  }

  private _suljePaneelit() {
    this.avoinPaneeli = null
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this._haeAlusta()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu = event.pageIndex
    this.sivunKoko = event.pageSize
    this._hae()
  }

  private _haeAlusta() {
    this.sivu = 0
    this._hae()
  }

  private _hae() {
    this._dataSource.hae(this.onkoHakuehtoja ? this._rakennaHakupyynto() : null)
  }

  vaihdaPaneeli(paneeli: LaskuHakuPaneeli) {
    this.avoinPaneeli = this.avoinPaneeli === paneeli ? null : paneeli
  }

  toggleViimeiset12kk() {
    this.viimeiset12kkAktiivinen = !this.viimeiset12kkAktiivinen
    if (this.viimeiset12kkAktiivinen) {
      asetaArvo(this.alkuPvm, null)
      asetaArvo(this.loppuPvm, null)
      this.aikavalinTeksti.set('')
    }
    this._haeAlusta()
  }

  valitseAikavali() {
    if (!this.alkuPvm().value() && !this.loppuPvm().value()) {
      return
    }
    this._paivitaAikavalinTeksti()
    this.avoinPaneeli = null
  }

  poistaAikavali() {
    this.viimeiset12kkAktiivinen = false
    asetaArvo(this.alkuPvm, null)
    asetaArvo(this.loppuPvm, null)
    this.aikavalinTeksti.set('')
    this._haeAlusta()
  }

  alkuPvmMuuttui() {
    if (this.alkuPvm().value()) {
      this.viimeiset12kkAktiivinen = false
    }
    this._paivitaAikavalinTeksti()
    this._haeAlusta()
  }

  loppuPvmMuuttui() {
    if (this.loppuPvm().value()) {
      this.viimeiset12kkAktiivinen = false
    }
    this._paivitaAikavalinTeksti()
    this._haeAlusta()
  }

  onkoTilaValittu(tila: LaskunTila): boolean {
    return this.valitutTilat.has(tila)
  }

  vaihdaTila(tila: LaskunTila) {
    if (this.valitutTilat.has(tila)) {
      this.valitutTilat.delete(tila)
    } else {
      this.valitutTilat.add(tila)
    }
    this._haeAlusta()
  }

  poistaTilaValinta(tila: LaskunTila) {
    this.valitutTilat.delete(tila)
    this._haeAlusta()
  }

  tyhjennaTilat() {
    if (this.valitutTilat.size === 0) {
      return
    }
    this.valitutTilat.clear()
    this._haeAlusta()
  }

  onkoAsiakasValittu(avain: string): boolean {
    return this.valitutAsiakkaat.has(avain)
  }

  vaihdaAsiakas(avain: string) {
    if (this.valitutAsiakkaat.has(avain)) {
      this.valitutAsiakkaat.delete(avain)
    } else {
      this.valitutAsiakkaat.add(avain)
    }
    this._haeAlusta()
  }

  poistaAsiakasValinta(avain: string) {
    this.valitutAsiakkaat.delete(avain)
    this._haeAlusta()
  }

  tyhjennaAsiakkaat() {
    if (this.valitutAsiakkaat.size === 0) {
      return
    }
    this.valitutAsiakkaat.clear()
    this._haeAlusta()
  }

  onkoProjektiValittu(avain: string): boolean {
    return this.valitutProjektit.has(avain)
  }

  vaihdaProjekti(avain: string) {
    if (this.valitutProjektit.has(avain)) {
      this.valitutProjektit.delete(avain)
    } else {
      this.valitutProjektit.add(avain)
    }
    this._haeAlusta()
  }

  poistaProjektiValinta(avain: string) {
    this.valitutProjektit.delete(avain)
    this._haeAlusta()
  }

  tyhjennaProjektit() {
    if (this.valitutProjektit.size === 0) {
      return
    }
    this.valitutProjektit.clear()
    this._haeAlusta()
  }

  get rajatutAsiakkaat(): TypeaheadAsiakas[] {
    const rajaus = this.asiakasRajaus().value().trim().toLowerCase()
    if (!rajaus) {
      return this.kaikkiAsiakkaat()
    }
    return this.kaikkiAsiakkaat().filter(asiakas => (asiakas.nimi ?? '').toLowerCase().includes(rajaus) || (asiakas.ytunnus ?? '').toLowerCase().includes(rajaus))
  }

  get valitutTilatLista(): TilaChip[] {
    return Array.from(this.valitutTilat).map(tila => ({ tila: tila, nimi: this._tilaNimet.get(tila) ?? '' }))
  }

  get valitutAsiakkaatLista(): ValittuRajaus[] {
    return Array.from(this.valitutAsiakkaat).map(avain => ({ avain: avain, nimi: this._asiakasNimet.get(avain) ?? '' }))
  }

  get valitutProjektitLista(): ValittuRajaus[] {
    return Array.from(this.valitutProjektit).map(avain => ({ avain: avain, nimi: this._projektiNimet.get(avain) ?? '' }))
  }

  get onkoHakuehtoja(): boolean {
    return (
      this.hakuteksti().value().trim().length > 0 ||
      this.viimeiset12kkAktiivinen ||
      this.alkuPvm().value() !== null ||
      this.loppuPvm().value() !== null ||
      this.valitutTilat.size > 0 ||
      this.valitutAsiakkaat.size > 0 ||
      this.valitutProjektit.size > 0
    )
  }

  async avaaLasku(rivi: LaskunListaustietorivi) {
    this._hakuStateService.lasku.tallennaHakutila({
      hakuteksti: this.hakuteksti().value(),
      viimeiset12kkAktiivinen: this.viimeiset12kkAktiivinen,
      alkuPvm: this.alkuPvm().value() ? this._dateService.dateToNumber(this.alkuPvm().value()) : null,
      loppuPvm: this.loppuPvm().value() ? this._dateService.dateToNumber(this.loppuPvm().value()) : null,
      valitutTilat: Array.from(this.valitutTilat),
      valitutAsiakkaat: Array.from(this.valitutAsiakkaat),
      naytaSummatVerottomina: this.naytaSummatVerottomina,
      valitutProjektit: Array.from(this.valitutProjektit),
      sivu: this.sivu,
      sivunKoko: this.sivunKoko,
      paluureitti: this._router.url,
    })
    const existing: LaskuKatseleComponentExistingData = {
      juurilasku: rivi.juurilasku,
      kasiteltava: rivi.kasiteltava,
    }
    this._laskuKatseleComponentDataResolve.asetaOlemassaolevaData(existing)
    const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    this._router.navigate(['asiakkaat', avainTiedot.avain, 'laskut', 'selaa', rivi.juuriAvain, rivi.avain])
  }

  private _rakennaHakupyynto(): LaskutHakuPyynto {
    const pyynto: LaskutHakuPyynto = { ylaraja: this.sivunKoko, sivu: this.sivu }

    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti
    }

    if (this.viimeiset12kkAktiivinen) {
      const tanaan = this._dateService.currentNumberDate()
      pyynto.alkuPvm = this._dateService.lisaaKuukausiaNumero(tanaan, -12)
      pyynto.loppuPvm = tanaan
    } else {
      if (this.alkuPvm().value()) {
        pyynto.alkuPvm = this._dateService.dateToNumber(this.alkuPvm().value())
      }
      if (this.loppuPvm().value()) {
        pyynto.loppuPvm = this._dateService.dateToNumber(this.loppuPvm().value())
      }
    }

    if (this.valitutTilat.size > 0) {
      pyynto.tilat = Array.from(this.valitutTilat)
    }
    if (this.valitutAsiakkaat.size > 0) {
      pyynto.asiakasAvaimet = Array.from(this.valitutAsiakkaat)
    }
    if (this.valitutProjektit.size > 0) {
      pyynto.projektiAvaimet = Array.from(this.valitutProjektit)
    }

    return pyynto
  }

  private _paivitaAikavalinTeksti() {
    if (!this.alkuPvm().value() && !this.loppuPvm().value()) {
      this.aikavalinTeksti.set('')
      return
    }

    if (!this._nykyinenKieli) {
      return
    }

    if (this.alkuPvm().value() && this.loppuPvm().value()) {
      const alkuStr = this._dateService.muotoilePaivaDate(this.alkuPvm().value(), this._nykyinenKieli)
      const loppuStr = this._dateService.muotoilePaivaDate(this.loppuPvm().value(), this._nykyinenKieli)
      this.aikavalinTeksti.set(`${alkuStr} - ${loppuStr}`)
    } else if (this.alkuPvm().value()) {
      const alkaenTeksti = this._translationService.lokalisoiKielella('laskut.haku.alkaen', this._nykyinenKieli)
      const alkuStr = this._dateService.muotoilePaivaDate(this.alkuPvm().value(), this._nykyinenKieli)
      this.aikavalinTeksti.set(`${alkaenTeksti} ${alkuStr}`)
    } else if (this.loppuPvm().value()) {
      const paattyenTeksti = this._translationService.lokalisoiKielella('laskut.haku.paattyen', this._nykyinenKieli)
      const loppuStr = this._dateService.muotoilePaivaDate(this.loppuPvm().value(), this._nykyinenKieli)
      this.aikavalinTeksti.set(`${paattyenTeksti} ${loppuStr}`)
    }
  }

  private _paivitaYhteissummat() {
    const laskurivit = this.tulokset().filter(rivi => rivi.tyyppi === LaskunListaustyyppi.TAVALLINEN || rivi.tyyppi === LaskunListaustyyppi.VANHEMPI)
    const kaikkiOsumatNakyvissa = this.kokonaismaara() > 0 && this.kokonaismaara() === laskurivit.length
    if (!kaikkiOsumatNakyvissa) {
      this.yhteissummaSisAlv.set(null)
      this.yhteissummaAlv0.set(null)
      return
    }
    this.yhteissummaSisAlv.set(this._laskeYhteissummaEuroina(laskurivit, false))
    this.yhteissummaAlv0.set(this._laskeYhteissummaEuroina(laskurivit, true))
  }

  private _laskeYhteissummaEuroina(laskurivit: LaskunListaustietorivi[], verottomana: boolean): number | null {
    let yhteissumma = 0
    for (const rivi of laskurivit) {
      const rivinSummaEuroina = this._annaRivinSummaEuroina(rivi, verottomana)
      if (rivinSummaEuroina === null) {
        return null
      }
      yhteissumma += rivinSummaEuroina
    }
    return this._currencyService.roundHalfUp(yhteissumma, 2)
  }

  private _annaRivinSummaEuroina(rivi: LaskunListaustietorivi, verottomana: boolean): number | null {
    const summa = verottomana ? rivi.summaAlv0 : rivi.summa
    if (rivi.valuutta === 'EUR') {
      return summa ?? 0
    }
    const summaEuroina = verottomana ? rivi.summaAlv0Euroina : rivi.summaEuroina
    return summaEuroina ?? null
  }
}
